import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useState, useRef } from "react";
import styled from "styled-components";

import { PrecalculatedAllowablesModal } from "../../../components/PrecalculatedAllowablesModal/PrecalculatedAllowablesModal";
import { parseDate } from "../../../helpers/date";
import { useGetUnderpaymentsQuery } from "../../../store/queries/underpayments";
import { Underpayment } from "../../../types/underpayment";

const CaseSignatureLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

interface IUnderpaymentsListProps {
  accountId: string;
}

function UnderpaymentsList({ accountId }: IUnderpaymentsListProps) {
  const toast = useRef<Toast>(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sorting state
  const [sortField, setSortField] = useState("serviceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Underpayment | null>(null);

  // Fetch data
  const { data, isLoading, isError, error } = useGetUnderpaymentsQuery({
    accountId,
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sortField,
    sortOrder,
    page,
    pageSize,
  });

  // Show error toast
  React.useEffect(() => {
    if (isError) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.data?.message || "Failed to load underpayments",
      });
    }
  }, [isError, error]);

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Template functions
  const dateTemplate = (
    rowData: Underpayment,
    field: "patientDOB" | "serviceDate"
  ) => {
    const date = rowData[field];
    return date ? parseDate(date) : "-";
  };

  const currencyTemplate = (
    rowData: Underpayment,
    field:
      | "chargeAmount"
      | "expectedAmount"
      | "claimAmount"
      | "amountDifference"
  ) => {
    return formatCurrency(rowData[field]);
  };

  const nullableTemplate = (value: string | null) => {
    return value || "-";
  };

  // Helper function to format case signature with line breaks (4 codes per line)
  const formatCaseSignatureWithLineBreaks = (caseSignature: string) => {
    // Split by comma to get array of "CODE:qty" items
    const items = caseSignature.split(",");

    // Group into chunks of 4
    const chunks: string[][] = [];
    for (let i = 0; i < items.length; i += 4) {
      chunks.push(items.slice(i, i + 4));
    }

    // Return JSX with each chunk on its own line
    return (
      <>
        {chunks.map((chunk, index) => (
          <div key={index}>{chunk.join(",")}</div>
        ))}
      </>
    );
  };

  // Case signature link template
  const caseSignatureTemplate = (rowData: Underpayment) => {
    // Format the case signature with line breaks
    const formattedSignature = formatCaseSignatureWithLineBreaks(
      rowData.caseSignature
    );

    // Only make it a link if we have all required data
    const hasAllowable = rowData.expectedAmount !== null && rowData.payerId;

    if (!hasAllowable) {
      // Just display as plain text if no allowable
      return <span>{formattedSignature}</span>;
    }

    const handleClick = () => {
      setSelectedRow(rowData);
      setModalVisible(true);
    };

    return (
      <CaseSignatureLink onClick={handleClick}>
        {formattedSignature}
      </CaseSignatureLink>
    );
  };

  // Accuracy status template - shows icon based on allowable accuracy
  const accuracyStatusTemplate = (rowData: Underpayment) => {
    if (!rowData.precalculatedAllowable) {
      return <span>-</span>;
    }

    const { allowableAccuracyStatus, missingCodes } =
      rowData.precalculatedAllowable;

    if (allowableAccuracyStatus) {
      return (
        <span
          title="Complete - All procedure codes found"
          style={{ color: "#28a745" }}
        >
          <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
        </span>
      );
    }

    const missingCount = missingCodes?.length || 0;
    return (
      <span
        title={`Incomplete - ${missingCount} code(s) missing`}
        style={{ color: "#ffc107" }}
      >
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "1.2rem" }}
        />
      </span>
    );
  };

  // Handle DataTable events
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1); // Reset to first page
  };

  const onPage = (event: any) => {
    setPage(event.page + 1); // PrimeReact uses 0-indexed pages
    setPageSize(event.rows);
  };

  // Handle filter changes - reset to page 1
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleStartDateChange = (value: Date | null) => {
    setStartDate(value);
    setPage(1);
  };

  const handleEndDateChange = (value: Date | null) => {
    setEndDate(value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
      </div>
    );
  }

  const underpayments = data?.data || [];
  const totalRecords = data?.totalCount || 0;

  return (
    <>
      <Toast ref={toast} />

      {/* Filters */}
      <div className="flex gap-3 mb-3 flex-wrap">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search patient or payer name..."
            className="w-20rem"
          />
        </span>

        <Calendar
          value={startDate}
          onChange={(e) => handleStartDateChange(e.value as Date | null)}
          placeholder="Start Date"
          showIcon
          dateFormat="mm/dd/yy"
        />

        <Calendar
          value={endDate}
          onChange={(e) => handleEndDateChange(e.value as Date | null)}
          placeholder="End Date"
          showIcon
          dateFormat="mm/dd/yy"
        />
      </div>

      {/* Data Table */}
      <DataTable
        value={underpayments}
        lazy
        paginator
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[10, 25, 50]}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage={data?.message || "No underpayments found."}
      >
        <Column
          field="patientName"
          header="Patient Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="patientDOB"
          header="Patient DOB"
          sortable
          body={(rowData) => dateTemplate(rowData, "patientDOB")}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="patientAccountNumber"
          header="Account #"
          sortable
          body={(rowData) => nullableTemplate(rowData.patientAccountNumber)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimNumber"
          header="Claim #"
          sortable
          body={(rowData) => nullableTemplate(rowData.claimNumber)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="serviceDate"
          header="Service Date"
          sortable
          body={(rowData) => dateTemplate(rowData, "serviceDate")}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimPayerName"
          header="Payer Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="claimPayerGroupNumber"
          header="Group #"
          body={(rowData) => nullableTemplate(rowData.claimPayerGroupNumber)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimNetworkStatus"
          header="Network Status"
          body={(rowData) => nullableTemplate(rowData.claimNetworkStatus)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="clearPayerName"
          header="Matched Payer"
          body={(rowData) => nullableTemplate(rowData.clearPayerName)}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="chargeAmount"
          header="Total Charges"
          sortable
          body={(rowData) => currencyTemplate(rowData, "chargeAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="claimAmount"
          header="Claim Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "claimAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="expectedAmount"
          header="Expected Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "expectedAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="amountDifference"
          header="Difference"
          sortable
          body={(rowData) => currencyTemplate(rowData, "amountDifference")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="allowableAccuracyStatus"
          header="Accuracy"
          sortable
          body={accuracyStatusTemplate}
          style={{ minWidth: "6rem", textAlign: "center" }}
        />
        <Column
          field="caseSignature"
          header="Case Signature"
          body={caseSignatureTemplate}
          style={{ minWidth: "12rem" }}
        />
      </DataTable>

      {/* Precalculated Allowables Modal */}
      {selectedRow && (
        <PrecalculatedAllowablesModal
          visible={modalVisible}
          onHide={() => {
            setModalVisible(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          payerId={selectedRow.payerId!}
          caseSignature={selectedRow.caseSignature}
        />
      )}
    </>
  );
}

export default UnderpaymentsList;
